import { useState } from "react";
import toast from "react-hot-toast";

import { ENV_VARS } from "@/constants/env";
import { getAuthTokensFromLocalStorage } from "@/utils/auth";
import { languageHeader } from "@/i18n/request-language";
import i18n from "@/i18n";

const extractFilename = (header: string | null, fallback: string): string => {
  if (!header) return fallback;
  const match = header.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i);
  return match?.[1] ? decodeURIComponent(match[1]) : fallback;
};

const triggerBlobDownload = (blob: Blob, fileName: string) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

export const useDownloadPaymentFile = () => {
  const [isDownloading, setIsDownloading] = useState(false);

  const fetchBlob = async (
    runId: number,
    fileId: number,
    fallback: string,
  ) => {
    const tokens = getAuthTokensFromLocalStorage();
    if (!tokens?.accessToken) throw new Error("Not authenticated");
    const baseUrl = ENV_VARS.API_INSTANCE.replace(/\/$/, "");
    const res = await fetch(
      `${baseUrl}/company/payroll/runs/${runId}/payment-files/${fileId}/download`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${tokens.accessToken}`,
          ...languageHeader(),
        },
      },
    );
    if (!res.ok) {
      let message = i18n.t("common:downloads.download_failed_status", {
        status: res.status,
      });
      try {
        const data = await res.json();
        if (data?.message) message = data.message;
      } catch {
        // not json
      }
      throw new Error(message);
    }
    const blob = await res.blob();
    const name = extractFilename(res.headers.get("content-disposition"), fallback);
    return { blob, name };
  };

  const download = (
    runId: number,
    fileId: number,
    { fileName }: { fileName?: string } = {},
  ) => {
    const fallback = fileName ?? `payment-file-${fileId}`;
    setIsDownloading(true);
    return toast.promise(
      fetchBlob(runId, fileId, fallback).then(({ blob, name }) => {
        triggerBlobDownload(blob, name);
      }),
      {
        loading: i18n.t("common:downloads.preparing_file"),
        success: i18n.t("common:downloads.file_downloaded"),
        error: (err) => err?.message ?? i18n.t("common:downloads.file_failed"),
      },
    ).finally(() => setIsDownloading(false));
  };

  return { download, isDownloading };
};
